// 受注数(130)/納品予定(131)の明細行を並べ替え、小計・合計行を組み立てる。
// 本文は Kakousiji.js の実装をそのまま移設したもの(品番マスタ側の簡易版はこれに統一)。
export function createDemandRowNormalizer(deps) {
    const {
        isEmpty,
        DicVal,
        resolveTypeColumnIdBySuffix,
        resolveTypeColumnIdByName,
        resolveTypeColumnNameById,
        JUTYU_SUMMARY_COL_ID,
        NOUHIN_SUMMARY_COL_ID
    } = deps;

    function normalizeJutyuRows(rows) {
        if (!Array.isArray(rows)) return rows;
        if (rows.length === 0) return rows;

        const isDetail = (r) => {
            const kind = String(r?.row_kind || "");
            if (kind === "subtotal" || kind === "total" || kind === "summary_label" || kind === "summary_total") return false;
            return true;
        };
        const details = rows.filter(isDetail);
        if (details.length === 0) return rows;

        const toNum = (v) => {
            const n = Number(String(v ?? "").replace(/,/g, ""));
            return Number.isFinite(n) ? n : 0;
        };
        const fmt = (n) => {
            if (!Number.isFinite(n)) return "0";
            if (Number.isInteger(n)) return String(n);
            return String(n);
        };

        const colorCol = resolveTypeColumnIdBySuffix("130", ["colorcd", "color"]) || resolveTypeColumnIdByName("130", ["カラー"]);
        const qtyCol = resolveTypeColumnIdBySuffix("130", ["jyucyusu", "suuryo", "suryo", "quantity", "qty"]) || resolveTypeColumnIdByName("130", ["数量", "受注数"]);
        if (isEmpty(qtyCol)) return rows;
        // 仕様確定: 受注数(type=130)の集計表示は意味付きColumnIDを使う。
        const jutyuSummaryColId = JUTYU_SUMMARY_COL_ID;
        const subtotalLabel = resolveTypeColumnNameById("130", jutyuSummaryColId.colorSubtotal);
        const totalLabel = resolveTypeColumnNameById("130", jutyuSummaryColId.totalLabel);
        // 仕様寄せ: 受注数はカラー昇順（同色内はサイズ昇順）で並べる。
        const sizeCol = resolveTypeColumnIdBySuffix("130", ["sizecd", "size"]) || resolveTypeColumnIdByName("130", ["サイズ"]);
        const ordered = [...details].sort((a, b) => {
            const ca = String(DicVal(a, colorCol, ""));
            const cb = String(DicVal(b, colorCol, ""));
            const ccmp = ca.localeCompare(cb, "ja", { numeric: true, sensitivity: "base" });
            if (ccmp !== 0) return ccmp;
            const sa = isEmpty(sizeCol) ? "" : String(DicVal(a, sizeCol, ""));
            const sb = isEmpty(sizeCol) ? "" : String(DicVal(b, sizeCol, ""));
            return sa.localeCompare(sb, "ja", { numeric: true, sensitivity: "base" });
        });

        const out = [];
        let currentColor = null;
        let subtotal = 0;
        let grandTotal = 0;
        let subtotalSeq = 0;
        let hasCurrentGroup = false;

        const flushSubtotal = () => {
            if (!hasCurrentGroup) return;
            const row = { rowid: "subtotal_" + subtotalSeq, row_kind: "subtotal" };
            subtotalSeq += 1;
            row[jutyuSummaryColId.colorSubtotal] = subtotalLabel;
            if (!isEmpty(colorCol)) row[colorCol] = subtotalLabel;
            row[qtyCol] = fmt(subtotal);
            out.push(row);
            subtotal = 0;
            hasCurrentGroup = false;
        };

        ordered.forEach((r) => {
            const colorVal = isEmpty(colorCol) ? "" : String(DicVal(r, colorCol, ""));
            if (currentColor === null) currentColor = colorVal;
            if (colorVal !== currentColor) {
                flushSubtotal();
                currentColor = colorVal;
            }

            const qty = toNum(DicVal(r, qtyCol, 0));
            subtotal += qty;
            grandTotal += qty;
            hasCurrentGroup = true;
            out.push(r);
        });
        flushSubtotal();

        const totalRow = { rowid: "total_qty", row_kind: "total" };
        totalRow[jutyuSummaryColId.totalLabel] = totalLabel;
        if (!isEmpty(colorCol)) totalRow[colorCol] = totalLabel;
        totalRow[qtyCol] = fmt(grandTotal);
        out.push(totalRow);
        return out;
    }
    function normalizeNouhinYoteiRows(rows) {
        if (!Array.isArray(rows)) return rows;
        if (rows.length === 0) return rows;

        const isDetail = (r) => {
            const kind = String(r?.row_kind || "");
            if (kind === "subtotal" || kind === "total" || kind === "summary_label" || kind === "summary_total") return false;
            return true;
        };
        const details = rows.filter(isDetail);
        if (details.length === 0) return rows;

        const toNum = (v) => {
            const n = Number(String(v ?? "").replace(/,/g, ""));
            return Number.isFinite(n) ? n : 0;
        };
        const fmt = (n) => {
            const num = Number(n);
            return String(Math.round(Number.isFinite(num) ? num : 0));
        };
        const summaryLabel = (value, fallback) => {
            const text = String(value ?? "").trim();
            if (text === "" || text === "Invalid Number") return fallback;
            return text;
        };
        const writeBoth = (row, keys, value) => {
            const uniq = [...new Set((Array.isArray(keys) ? keys : []).filter(k => !isEmpty(k)))];
            uniq.forEach((k) => { row[k] = value; });
        };
        const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj ?? {}, key);
        const firstExistingCol = (candidates, sourceRows) => {
            const keys = (Array.isArray(candidates) ? candidates : [])
                .map(v => String(v ?? "").trim())
                .filter(v => v !== "");
            if (keys.length === 0) return "";
            for (let i = 0; i < keys.length; i++) {
                const k = keys[i];
                if ((sourceRows || []).some(r => hasOwn(r, k))) return k;
            }
            return keys[0];
        };
        const metricValue = (row, candidates) => {
            const keys = (Array.isArray(candidates) ? candidates : [])
                .map(v => String(v ?? "").trim())
                .filter(v => v !== "");
            for (let i = 0; i < keys.length; i++) {
                const k = keys[i];
                if (!hasOwn(row, k)) continue;
                return toNum(row[k]);
            }
            return 0;
        };
        const colorCol = resolveTypeColumnIdByName("131", ["カラー"]) || "SSDNY_COLORCD";
        const sizeCol = resolveTypeColumnIdByName("131", ["サイズ"]) || "SSDNY_SIZECD";
        // 仕様確定: 納品予定(type=131)の集計表示は意味付きColumnIDを使う。
        const nouhinSummaryColId = NOUHIN_SUMMARY_COL_ID;
        const nouhinSummaryLabel = {
            toinyu: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.toinyuTotal), "投入数計"),
            siirejisseki: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.siireTotal), "仕入実績計"),
            nouhinyotei: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.nouhinTotal), "納品予定計"),
            center: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.centerTotal), "センター納品計"),
            bunno1: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.part1Total), "分納1計"),
            bunno2: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.part2Total), "分納2計"),
            bunno3: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.part3Total), "分納3計"),
            bunno4: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.part4Total), "分納4計"),
            zansu: summaryLabel(resolveTypeColumnNameById("131", nouhinSummaryColId.zansuTotal), "残数計")
        };
        const zansuViewCol = nouhinSummaryColId.zansu;
        const toinyuCandidates = [
            resolveTypeColumnIdBySuffix("131", ["mdhatchusu"]),
            resolveTypeColumnIdByName("131", ["投入数"]),
            "TNYC_MDHATCHUSU"
        ];
        const nouhinCandidates = [
            resolveTypeColumnIdBySuffix("131", ["nouhinyoteisu"]),
            resolveTypeColumnIdByName("131", ["納品予定", "納品予定数"]),
            "SSDNY_NOUHINYOTEI_SU"
        ];
        const centerCandidates = [
            resolveTypeColumnIdBySuffix("131", ["centersu"]),
            resolveTypeColumnIdByName("131", ["センター納品", "センター納品数"]),
            "SSDNY_CENTER_SU"
        ];
        const part1Candidates = [
            resolveTypeColumnIdBySuffix("131", ["part1su"]),
            resolveTypeColumnIdByName("131", ["分納1", "分納1数"]),
            "SSDNY_PART1_SU"
        ];
        const part2Candidates = [
            resolveTypeColumnIdBySuffix("131", ["part2su"]),
            resolveTypeColumnIdByName("131", ["分納2", "分納2数"]),
            "SSDNY_PART2_SU"
        ];
        const part3Candidates = [
            resolveTypeColumnIdBySuffix("131", ["part3su"]),
            resolveTypeColumnIdByName("131", ["分納3", "分納3数"]),
            "SSDNY_PART3_SU"
        ];
        const part4Candidates = [
            resolveTypeColumnIdBySuffix("131", ["part4su"]),
            resolveTypeColumnIdByName("131", ["分納4", "分納4数"]),
            "SSDNY_PART4_SU"
        ];
        const displayCols = {
            toinyu: resolveTypeColumnIdByName("131", ["投入数"]) || resolveTypeColumnIdBySuffix("131", ["mdhatchusu"]),
            siirejisseki: resolveTypeColumnIdByName("131", ["仕入実績", "仕入実績数"]) || resolveTypeColumnIdBySuffix("131", ["siiresu"]),
            nouhinyotei: resolveTypeColumnIdByName("131", ["納品予定", "納品予定数"]) || resolveTypeColumnIdBySuffix("131", ["nouhinyoteisu"]),
            center: resolveTypeColumnIdByName("131", ["センター納品", "センター納品数"]) || resolveTypeColumnIdBySuffix("131", ["centersu"]),
            bunno1: resolveTypeColumnIdByName("131", ["分納1", "分納1数"]) || resolveTypeColumnIdBySuffix("131", ["part1su"]),
            bunno2: resolveTypeColumnIdByName("131", ["分納2", "分納2数"]) || resolveTypeColumnIdBySuffix("131", ["part2su"]),
            bunno3: resolveTypeColumnIdByName("131", ["分納3", "分納3数"]) || resolveTypeColumnIdBySuffix("131", ["part3su"]),
            bunno4: resolveTypeColumnIdByName("131", ["分納4", "分納4数"]) || resolveTypeColumnIdBySuffix("131", ["part4su"]),
            zansu: resolveTypeColumnIdByName("131", ["残数"])
        };
        let zansuCandidates = [
            nouhinSummaryColId.zansu,
            displayCols.zansu
        ];
        // 仕様寄せ: 納品予定数はカラー昇順（同色内はサイズ昇順）で並べる。
        const ordered = [...details].sort((a, b) => {
            const ca = String(DicVal(a, colorCol, ""));
            const cb = String(DicVal(b, colorCol, ""));
            const ccmp = ca.localeCompare(cb, "ja", { numeric: true, sensitivity: "base" });
            if (ccmp !== 0) return ccmp;
            const sa = isEmpty(sizeCol) ? "" : String(DicVal(a, sizeCol, ""));
            const sb = isEmpty(sizeCol) ? "" : String(DicVal(b, sizeCol, ""));
            return sa.localeCompare(sb, "ja", { numeric: true, sensitivity: "base" });
        });
        // 業務仕様: 各行の残数 = 納品予定数 - (センター納品数 + 分納1 + 分納2 + 分納3 + 分納4)
        const zansuSyncCols = [...new Set([
            zansuViewCol,
            displayCols.zansu
        ].filter(v => !isEmpty(v)))];
        const zansuWriteCol = firstExistingCol(zansuSyncCols, ordered);
        const normalizedDetails = ordered.map((r) => {
            const row = { ...r };
            const nouhin = metricValue(row, nouhinCandidates);
            const center = metricValue(row, centerCandidates);
            const p1 = metricValue(row, part1Candidates);
            const p2 = metricValue(row, part2Candidates);
            const p3 = metricValue(row, part3Candidates);
            const p4 = metricValue(row, part4Candidates);
            const zansuValue = nouhin - (center + p1 + p2 + p3 + p4);
            writeBoth(row, zansuSyncCols, zansuValue);
            return row;
        });
        zansuCandidates = [zansuWriteCol, ...zansuCandidates];
        const metricDefs = [
            {
                key: "toinyu",
                label: nouhinSummaryLabel.toinyu,
                viewCol: nouhinSummaryColId.toinyuTotal,
                displayCol: displayCols.toinyu,
                candidates: toinyuCandidates
            },
            {
                key: "siirejisseki",
                label: nouhinSummaryLabel.siirejisseki,
                viewCol: nouhinSummaryColId.siireTotal,
                displayCol: displayCols.siirejisseki,
                candidates: [
                    resolveTypeColumnIdBySuffix("131", ["siiresu"]),
                    resolveTypeColumnIdByName("131", ["仕入実績", "仕入実績数"]),
                    "SSRM_SIIRESU"
                ]
            },
            {
                key: "nouhinyotei",
                label: nouhinSummaryLabel.nouhinyotei,
                viewCol: nouhinSummaryColId.nouhinTotal,
                displayCol: displayCols.nouhinyotei,
                candidates: nouhinCandidates
            },
            {
                key: "center",
                label: nouhinSummaryLabel.center,
                viewCol: nouhinSummaryColId.centerTotal,
                displayCol: displayCols.center,
                candidates: centerCandidates
            },
            {
                key: "bunno1",
                label: nouhinSummaryLabel.bunno1,
                viewCol: nouhinSummaryColId.part1Total,
                displayCol: displayCols.bunno1,
                candidates: part1Candidates
            },
            {
                key: "bunno2",
                label: nouhinSummaryLabel.bunno2,
                viewCol: nouhinSummaryColId.part2Total,
                displayCol: displayCols.bunno2,
                candidates: part2Candidates
            },
            {
                key: "bunno3",
                label: nouhinSummaryLabel.bunno3,
                viewCol: nouhinSummaryColId.part3Total,
                displayCol: displayCols.bunno3,
                candidates: part3Candidates
            },
            {
                key: "bunno4",
                label: nouhinSummaryLabel.bunno4,
                viewCol: nouhinSummaryColId.part4Total,
                displayCol: displayCols.bunno4,
                candidates: part4Candidates
            },
            {
                key: "zansu",
                label: nouhinSummaryLabel.zansu,
                viewCol: nouhinSummaryColId.zansuTotal,
                displayCol: displayCols.zansu,
                candidates: zansuCandidates
            }
        ].map((d) => {
            const readCol = firstExistingCol(d.candidates, normalizedDetails);
            const writeCol = !isEmpty(d.viewCol) ? d.viewCol : readCol;
            const displayCol = !isEmpty(d.displayCol) ? d.displayCol : readCol;
            const mirrorToDisplay = !isEmpty(displayCol) && displayCol !== writeCol;
            return { ...d, readCol: readCol, writeCol: writeCol, displayCol: displayCol, mirrorToDisplay: mirrorToDisplay };
        }).filter(d => !isEmpty(d.readCol) && !isEmpty(d.writeCol));

        if (metricDefs.length === 0) return rows;
        const missingQuantityDefaultKeys = new Set(["toinyu", "siirejisseki", "nouhinyotei", "center", "bunno1", "bunno2", "bunno3", "bunno4"]);
        normalizedDetails.forEach((row) => {
            metricDefs.forEach((d) => {
                if (!missingQuantityDefaultKeys.has(d.key)) return;
                if (hasOwn(row, d.readCol)) return;
                row[d.readCol] = 0;
                if (d.mirrorToDisplay && !hasOwn(row, d.displayCol)) {
                    row[d.displayCol] = 0;
                }
            });
        });

        const sums = {};
        metricDefs.forEach(d => { sums[d.key] = 0; });
        normalizedDetails.forEach((r) => {
            metricDefs.forEach((d) => {
                // 残数計は「表示している残数列(-)」の単純合計を採用する。
                if (d.key === "zansu") {
                    sums[d.key] += toNum(DicVal(r, zansuViewCol, DicVal(r, d.readCol, 0)));
                    return;
                }
                sums[d.key] += metricValue(r, d.candidates);
            });
        });

        const labelRow = { rowid: "summary_label", row_kind: "summary_label" };
        const totalRow = { rowid: "summary_total", row_kind: "summary_total" };
        if (!isEmpty(colorCol)) {
            labelRow[colorCol] = "";
            totalRow[colorCol] = "計";
        }
        if (!isEmpty(sizeCol)) {
            labelRow[sizeCol] = "";
            totalRow[sizeCol] = "";
        }
        metricDefs.forEach((d) => {
            const targets = d.mirrorToDisplay ? [d.writeCol, d.displayCol] : [d.writeCol];
            if (d.key === "zansu") {
                writeBoth(labelRow, targets, d.label);
                writeBoth(totalRow, targets, Number.isFinite(Number(sums[d.key])) ? Math.round(Number(sums[d.key])) : 0);
                return;
            }
            writeBoth(labelRow, targets, d.label);
            writeBoth(totalRow, targets, fmt(sums[d.key]));
        });
        return [...normalizedDetails, labelRow, totalRow];
    }

    return {
        normalizeJutyuRows,
        normalizeNouhinYoteiRows
    };
}
